import { observer } from 'mobx-react';
import { RESTRICTION_ACCESS } from '~/constants/integrations';
import integrationsStore from '~/mobx/integrationStore';
import { localeStore } from '~/mobx/localesStore';
import { modalStore } from '~/mobx/modalStore';
import askAgain, { AskAgainKey } from '~/services/askAgain';
import { BRANCHES_MODAL_FETCH_PARAMETER, IBranch, UNAVAILABLE_BRANCHES_FLAG } from '~/types/branches';
import { LoadingStatus } from '~/types/main';
import { getTranslate, scrollToElement } from '~/utils/helpers';
import { IAskAgainModalSubmit } from '~/view/containers/Modals/AskAgainModal/AskAgainModal';
import { BranchesModalProfile, IBranchesModalItem } from '~/view/containers/Modals/BranchesModal/BranchesModal';
import ProfileBranches from '~/view/containers/ProfileBranches/ProfileBranches';
import { branchesWorkingHoursStore } from '~/view/screens/BranchesManagementScreen/screens/BranchesWorkingHours/store/branchesWorkingHoursStore';

const HoursProfileBranches = observer(() => {
  const openBranchesModal = () => {
    modalStore.openBranchesModal({
      selectedBranches: branchesWorkingHoursStore.selectedProfile?.branches || [],
      onSave: handleBranchesUpdate,
      headerTitle: localeStore.t('adding_branches_to_profile_for_fast'),
      isMulti: true,
      askAgainChecker,
      askAgainHandler,
      shouldCheckPosConflict: integrationsStore.posRestrictions?.branchWorkingHours === RESTRICTION_ACCESS.DISABLED,
      saveWithoutBranches: true,
      fetchParamStatus: BRANCHES_MODAL_FETCH_PARAMETER.ALL,
      fetchParamDelete: BRANCHES_MODAL_FETCH_PARAMETER.ACTIVE,
      checkUnavailable: true,
      unavailableFlag: UNAVAILABLE_BRANCHES_FLAG.DELETE,
      highlightUnavailable: true,
      profileNamesToShow: [BranchesModalProfile.FRANCHISE, BranchesModalProfile.HOURS],
    });
  };

  const handleBranchesUpdate = (branches: IBranchesModalItem[]) => {
    branchesWorkingHoursStore.updateProfileBranches({ branchIds: branches.map((branch) => branch.id) });
    modalStore.closeBranchesModal();
  };

  const handleBranchDelete = (branchId: number) => {
    branchesWorkingHoursStore.updateProfileBranches({ deletedBranchId: branchId });
  };

  const handleProfileDuplicate = async (profileId: number) => {
    await branchesWorkingHoursStore.duplicateProfile(profileId);
    scrollToElement({ id: `profile_${profileId}` });
  };

  const askAgainChecker = (branch: IBranch | undefined): boolean => {
    if (!branch || !branch.branch_profile_id) return false;
    return branch.branch_profile_id !== branchesWorkingHoursStore.selectedProfileId;
  };

  const askAgainHandler = (branch: IBranch | undefined, cb: (branch: IBranch, data: IAskAgainModalSubmit) => void) => {
    if (!branch || !branch.branch_profile_id) return true;

    askAgain.showModal(
      {
        submit: (data) => askAgainSubmit(data, branch, cb),
        message: `${localeStore.t('are_you_sure_want_to_change_profile')} ${getTranslate(branch.name)} ${localeStore.t('txt_from')} ${
          branch.branch_profile_name
        } ${localeStore.t('txt_select_to')} ${branchesWorkingHoursStore.selectedProfile?.name} ?`,
      },
      AskAgainKey.FRANCHISE_PROFILE
    );
  };

  const askAgainSubmit = (data: IAskAgainModalSubmit, branch: IBranch, callback: (branch: IBranch, data: IAskAgainModalSubmit) => void) => {
    askAgain.hideModal(AskAgainKey.FRANCHISE_PROFILE, data.checkBoxStatus);
    callback(branch, data);
  };

  return (
    <ProfileBranches
      selectedProfile={branchesWorkingHoursStore.selectedProfile}
      onDuplicateProfile={handleProfileDuplicate}
      onDeleteProfile={branchesWorkingHoursStore.deleteProfile}
      onDeleteBranch={handleBranchDelete}
      onBranchesModalOpen={openBranchesModal}
      deleteConfirmationOptions={{
        title: localeStore.t('deactivate_branch'),
        message: localeStore.t('msg_deactivate_branch'),
        confirmBtnTxt: localeStore.t('deactivate'),
      }}
      loading={branchesWorkingHoursStore.loadingStatus === LoadingStatus.Loading}
      notEditable={branchesWorkingHoursStore.isProfileNotEditable}
    />
  );
});

export default HoursProfileBranches;
